import { TOKEN } from '../contexts/authContext';
import { SIGNIN } from '../routes/paths';

export class ApiService {
    private readonly baseUrl?: string;

    constructor() {
        this.baseUrl = process.env.REACT_APP_URL_LOCAL;
    }

    async get<T>(path: string, params?: Record<string, any>): Promise<T> {
        return this.request<T>('GET', path, undefined, params);
    }

    async post<T>(
        path: string,
        data: any,
        params?: Record<string, any>,
        headers?: HeadersInit | undefined
    ): Promise<T> {
        return this.request<T>('POST', path, data, params, headers);
    }

    async patch<T>(path: string, data?: any): Promise<T> {
        return this.request<T>('PUT', path, data);
    }

    async delete(path: string, data?: any): Promise<void> {
        return this.request<void>('DELETE', path, data);
    }

    private async request<T>(
        method: string,
        path: string,
        data?: any,
        params?: Record<string, any>,
        headers?: HeadersInit | undefined
    ): Promise<T> {
        let url = `${this.baseUrl}${path}`;

        if (params) {
            const urlFormater = new URL(url);
            Object.entries(params).forEach(([key, value]) => {
                if (typeof value === 'object') {
                    urlFormater.searchParams.append(key, JSON.stringify(value));
                } else {
                    urlFormater.searchParams.append(key, value);
                }
            });
            url = urlFormater.toString();
        }

        const isFormData =
            typeof FormData !== 'undefined' && data instanceof FormData;

        const response = await fetch(url, {
            method,
            headers: headers || {
                accept: '*/*',
                Authorization: `Bearer ${localStorage.getItem(TOKEN)}`,
                'Content-Type': 'application/json',
            },
            body: isFormData ? data : JSON.stringify(data),
        });

        if (!response.ok) {
            let errorBody: any = null;

            try {
                errorBody = await response.clone().json();
            } catch (_) {}

            const status = response.status;
            const message = errorBody?.message;

            const forbiddenExceptions = [
                'USER-TYPE-MISSING',
                'USER-ROL-INVALID',
                '[CHAT_ASSISTANT] FORBIDDEN',
            ];

            const isForbiddenException =
                status === 403 && forbiddenExceptions.includes(message);

            const shouldRedirect =
                (status === 401 || (status === 403 && !isForbiddenException)) &&
                window.location.pathname !== SIGNIN;

            if (shouldRedirect) {
                localStorage.clear();
                window.location.href = SIGNIN;
                return Promise.reject(errorBody);
            }

            console.error(
                `Failed to ${method.toLowerCase()} data at ${url}`,
                errorBody
            );
            return Promise.reject(errorBody);
        }
        return response.json() as Promise<T>;
    }

    async download(
        path: string
    ): Promise<{ blob: Blob; filename: string } | null> {
        try {
            const url = `${this.baseUrl}${path}`;

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    accept: '*/*',
                    Authorization: `Bearer ${localStorage.getItem(TOKEN)}`,
                },
            });

            if (!response.ok) {
                console.error(
                    `Error descargando archivo desde ${url}`,
                    response.statusText
                );
                return null;
            }

            const blob = await response.blob();

            const contentDisposition = response.headers.get(
                'content-disposition'
            );

            const match = contentDisposition?.match(/filename="?([^"]+)"?/);

            const filename = match?.[1] || 'archivo.png';

            return { blob, filename };
        } catch (error) {
            console.error(`Error en la descarga de archivo:`, error);
            return null;
        }
    }
}

export const apiService = new ApiService();
